import React, { useState, useEffect } from 'react';
import { CButton, CModal, CModalHeader, CModalBody, CModalTitle, CModalFooter } from '@coreui/react';
import { BsArrowRight } from 'react-icons/bs';
import { FaEdit } from "react-icons/fa";
import Profilimg from '../../../assets/images/man-438081_960_720.png';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { ToastContainer, toast } from 'react-toastify';
import api from '../../../service/caller';

const animatedComponents = makeAnimated();

const EditStaffModal = ({ visible, onClose, initialData, onStaffUpdated, user }) => {

    const [formData, setFormData] = useState({
        hospital_id: [],
        departementId: [],
        specialties: [],
        firstname: '',
        lastname: '',
        username: '',
        type: '',
        civility: '',
        role: '',
        email: '',
        password: '',
        confirmPassword: '',
        contact: {
            phone: '',
            address: '',
        },
        permissions: [],
        picture: ''
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(Profilimg);

    // Effet pour pré-remplir les données de l'utilisateur dans le formulaire lorsque visible est true
    useEffect(() => {
        if (user && visible) {
            setFormData({
                hospital_id: user.hospital_id.map(hospital => hospital._id),
                departementId: user.departementId.map(dep => dep._id),
                specialties: user.specialties.length === 0 ? ['no_specialty'] : user.specialties.map(spec => spec._id),
                firstname: user.firstname || '',
                lastname: user.lastname || '',
                username: user.username || '',
                type: user.type || '',
                civility: user.civility || '',
                role: user.role?._id || '',
                email: user.email || '',
                contact: {
                    phone: user.contact?.phone || '',
                    address: user.contact?.address || '',
                },
                permissions: user.permissions || [],
            });
            setPreviewImage(user.picture)
        }
    }, [user, visible]);


    const [Specialities, setSpecialities] = useState([]);
    const [Hospitals, setHospitals] = useState([]);
    const [Departments, setDepartments] = useState([]);
    const [Roles, setRoles] = useState([]);
    // Add states for permissions
    const [allPermissions, setAllPermissions] = useState([]);
    const [filteredPermissions, setFilteredPermissions] = useState([]);
    const [errors, setErrors] = useState({});
    // const [filteredSpecialities, setFilteredSpecialities] = useState([]);

    useEffect(() => {
        // Pré-remplissage des données si `initialData` est fourni
        if (initialData) {
            setFormData({
                ...initialData,
                specialties: initialData.specialties || [],
                // Add permissions field
                permissions: initialData.permissions || [],
                contact: initialData.contact || { phone: "", address: "" }
            });
        }
    }, [initialData]);

    useEffect(() => {
        const fetchData = async () => {

            if (!formData.hospital_id || formData.hospital_id.length === 0) return;

            try {
                const [hospitals, departments, roles] = await Promise.all([
                    api.get(`api/hospitals/admin/${localStorage.getItem('access_token')}`),
                    api.post(`/api/departments/byhospitals`, {
                        hospitalIds: formData.hospital_id
                    }),
                    api.get('api/getallroles')
                ]);

                setHospitals(hospitals.data);
                setDepartments(departments.data.data);
                setRoles(roles.data);

                // Extract all unique permissions from roles
                const permissionsSet = new Set();
                roles.data.forEach(role => {
                    role.permissions.forEach(permission => {
                        permissionsSet.add(permission);
                    });
                });

                setAllPermissions(Array.from(permissionsSet));

                // Initialize filtered permissions based on user's role
                if (user?.role?._id) {
                    const selectedRole = roles.data.find(role => role._id === user.role._id);
                    if (selectedRole) {
                        setFilteredPermissions(selectedRole.permissions);
                    }
                }
            } catch (error) {
                toast.error("Failed to fetch data");
            }
        };
        fetchData();
    }, [user, formData.hospital_id]);

    // Update filtered permissions when role changes
    useEffect(() => {
        if (formData.role && Roles.length > 0) {
            const selectedRole = Roles.find(role => role._id === formData.role);
            if (selectedRole) {
                setFilteredPermissions(selectedRole.permissions);

                // If this is a role change (not initial load), update permissions
                // Only auto-update permissions if the user hasn't customized them yet
                if (formData.permissions.length === 0 ||
                    JSON.stringify(formData.permissions.sort()) ===
                    JSON.stringify(filteredPermissions.sort())) {
                    setFormData(prevData => ({
                        ...prevData,
                        permissions: selectedRole.permissions
                    }));
                }
            }
        }
    }, [formData.role, Roles]);

    const handleHospitalChange = (selectedOptions) => {
        setFormData(prev => ({
            ...prev,
            hospital_id: selectedOptions ? selectedOptions.map(opt => opt.value) : []
        }));
    };

    useEffect(() => {
        const loadSpecialities = async () => {
            const selectedIds = formData.departementId || [];
            if (selectedIds.length > 0) {
                try {
                    const res = await api.post('/api/specialities/bydepartments', {
                        departmentIds: selectedIds,
                    });
                    setSpecialities(res.data.data);
                } catch (error) {
                    console.error("Erreur lors du chargement des spécialités");
                }
            } else {
                setSpecialities([]);
            }
        };

        loadSpecialities();
    }, [formData.departementId]);

    const handleDepartmentChange = async (selectedOptions) => {
        const selectedIds = selectedOptions.map(opt => opt.value);

        // Si "all" est sélectionné
        if (selectedIds.includes('all')) {
            setFormData(prev => ({
                ...prev,
                departementId: ['all'],
            }));

            try {
                const responses = await Promise.all(
                    formData.hospital_id.map(hospitalId =>
                        api.get(`/api/specialities/hospital/${hospitalId}`)
                    )
                );

                const allSpecialities = responses.flatMap(response =>
                    Array.isArray(response.data) ? response.data :
                        Array.isArray(response.data?.data) ? response.data.data :
                            []
                );

                const uniqueSpecialities = Array.from(new Map(
                    allSpecialities.filter(item => item?._id)
                        .map(item => [item._id, item])
                ).values());

                setSpecialities(uniqueSpecialities);

                // Filtrer les spécialités qui existent encore
                const validSpecialties = formData.specialties.filter(spec =>
                    uniqueSpecialities.some(s => s._id === spec)
                );

                setFormData(prev => ({
                    ...prev,
                    specialties: validSpecialties,
                }));

            } catch (error) {
                console.error("Échec de la récupération des spécialités");
                setSpecialities([{ label: 'No Specialty', value: 'no_specialty' }]);
            }

            return;
        }

        // Sinon, gestion classique
        setFormData(prev => ({
            ...prev,
            departementId: selectedIds
        }));

        try {
            const res = await api.post('/api/specialities/bydepartments', {
                departmentIds: selectedIds,
            });

            const specialtiesList = res.data.data || [];

            setSpecialities([
                { label: "No Specialty", value: "no_specialty" },
                ...specialtiesList.map(spec => ({
                    label: spec.name,
                    value: spec._id
                }))
            ]);

            const validSpecialties = formData.specialties.filter(spec =>
                specialtiesList.some(s => s._id === spec)
            );

            setFormData(prev => ({
                ...prev,
                specialties: validSpecialties,
            }));

        } catch (error) {
            console.error("Échec de la récupération des spécialités");
            setSpecialities([{ label: 'No Specialty', value: 'no_specialty' }]);
        }
    };



    const handleSpecialtiesChange = (selectedOptions) => {
        const selectedValues = selectedOptions ? selectedOptions.map((option) => option.value) : [];

        if (selectedValues.includes('no_specialty')) {
            setFormData(prev => ({
                ...prev,
                specialties: ['no_specialty']
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            specialties: selectedValues
        }));
    };



    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes("contact.")) {
            const key = name.split(".")[1];
            setFormData((prevData) => ({
                ...prevData,
                contact: {
                    ...prevData.contact,
                    [key]: value
                }
            }));
        } else {
            setFormData((prevData) => ({
                ...prevData,
                [name]: value
            }));
        }
    };

    // Handler for role change
    const handleRoleChange = (e) => {
        const roleId = e.target.value;

        // Find selected role and its permissions
        const selectedRole = Roles.find(role => role._id === roleId);
        const rolePermissions = selectedRole ? selectedRole.permissions : [];

        setFormData((prevData) => ({
            ...prevData,
            role: roleId,
            // Pre-fill permissions with the ones from the selected role
            permissions: rolePermissions
        }));

        // Update available permissions based on role
        if (roleId) {
            if (selectedRole) {
                setFilteredPermissions(selectedRole.permissions);
            } else {
                setFilteredPermissions([]);
            }
        } else {
            setFilteredPermissions([]);
        }
    };

    // Handler for permissions change
    const handlePermissionsChange = (selectedOptions) => {
        setFormData((prevData) => ({
            ...prevData,
            permissions: selectedOptions ? selectedOptions.map((option) => option.value) : [],
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.type) newErrors.type = "Type is required";
        if (!formData.civility) newErrors.civility = "Civility is required";
        if (!formData.firstname) newErrors.firstname = "Firstname is required";
        if (!formData.lastname) newErrors.lastname = "Lastname is required";
        if (!formData.username) newErrors.username = "Username is required";
        if (!formData.email) newErrors.email = "Email is required";
        if (!formData.role) newErrors.role = "Role is required";
        if (!formData.departementId) newErrors.departementId = "Department is required";
        if (!formData.hospital_id) newErrors.hospital_id = "Hospital is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewImage(URL.createObjectURL(file)); // Affichage de l'aperçu
        }
    };

    // Nettoyer l'URL créée quand le composant est démonté ou qu'une nouvelle image est chargée

    useEffect(() => {
        return () => {
            if (previewImage) {
                URL.revokeObjectURL(previewImage);
            }
        };
    }, [previewImage]);


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.password !== formData.confirmPassword) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                confirmPassword: "Les mots de passe ne correspondent pas.",
            }));
            return;
        }

        setErrors((prevErrors) => ({
            ...prevErrors,
            confirmPassword: null,
        }));

        if (validateForm()) {
            try {
                // Create FormData instance for file upload
                const submitData = new FormData();

                // Append profile picture if selected
                if (selectedFile) {
                    submitData.append('picture', selectedFile);
                }

                // Append all form data fields
                Object.keys(formData).forEach(key => {
                    if (key === 'contact') {
                        // Handle nested contact object
                        submitData.append('contact[phone]', formData.contact.phone);
                        submitData.append('contact[address]', formData.contact.address);
                    } else if (Array.isArray(formData[key])) {
                        submitData.append(key, JSON.stringify(formData[key]));
                    } else {
                        submitData.append(key, formData[key]);
                    }
                });

                // Send the request with FormData
                const response = await api.put(`api/users/${user?._id}`, submitData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });

                toast.success("Staff updated successfully!");
                onStaffUpdated(response.data);
                onClose();
            } catch (error) {
                const errorMessage = error.response?.data.error || error.message || "An unknown error occurred";
                toast.error(`Failed to update staff: ${errorMessage}. Please try again.`);
            }
        }
    };

    return (
        <CModal
            className='newregistermodal'
            alignment="center"
            scrollable
            size='lg'
            visible={visible}
            onClose={onClose}
        >
            <CModalHeader>
                <CModalTitle className='Titleformsmodal'>Edit Staff</CModalTitle>
            </CModalHeader>
            <CModalBody className='p-5'>
                <form onSubmit={handleSubmit}>
                    <ToastContainer />

                    {/* Profile Picture */}
                    <div className="form-group">
                        <div className="profile-photo d-flex">
                            <div className="photo-container">
                                <img src={ previewImage} alt="Profile" className="profile-image" />
                            </div>
                            <div className='photoedit'>
                                <label htmlFor="file-input">
                                    <FaEdit />
                                </label>
                                <input type="file" id="file-input" style={{ opacity: 0, position: "absolute", width: 0, height: 0 }} accept="image/*" onChange={handleFileChange} />
                                <p className='ms-2'>Upload profile photo (jpg, png, jpeg)</p>
                            </div>
                        </div>
                    </div>

                    {/* Hospital Select */}
                    <div className="form-group firstformgroup">
                        <label htmlFor="hospital_id">Hospital</label>
                        <Select
                            isMulti
                            name="hospital_id"
                            id="hospital_id"
                            options={Hospitals.map(hospital => ({
                                label: hospital.hospital_name,
                                value: hospital._id,
                            }))}
                            value={Hospitals.filter(hospital => formData.hospital_id.includes(hospital._id))
                                .map(hospital => ({
                                    label: hospital.hospital_name,
                                    value: hospital._id,
                                }))}
                            onChange={handleHospitalChange}
                            isSearchable
                            placeholder="Select Hospitals"
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    borderColor: '#0056B3',
                                    border: '2px solid #0056B3',
                                    borderRadius: '10px',
                                    minHeight: '50px',
                                }),
                            }}
                        />
                        {errors.hospital_id && <div className="text-danger">{errors.hospital_id}</div>}
                    </div>

                    <>
                        {/* Department */}
                        <div className="form-group firstformgroup">
                            <label htmlFor="departementId">Department</label>
                            <Select
                                isMulti
                                name="departementId"
                                id="departementId"
                                options={[
                                    { label: 'All Departments', value: 'all' },
                                    ...Departments.map(dep => ({
                                        label: dep.name,
                                        value: dep._id,
                                    }))
                                ]}
                                value={[
                                    ...(
                                        formData.departementId.includes("all")
                                            ? [{ label: 'All Departments', value: 'all' }]
                                            : Departments
                                                .filter(dep => formData.departementId.includes(dep._id))
                                                .map(dep => ({ label: dep.name, value: dep._id }))
                                    )
                                ]}
                                onChange={handleDepartmentChange}
                                isSearchable
                                placeholder="Select Departments"
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        borderColor: '#0056B3',
                                        border: '2px solid #0056B3',
                                        borderRadius: '10px',
                                        minHeight: '50px',
                                    }),
                                }}
                            />
                            {errors.departementId && <div className="text-danger">{errors.departementId}</div>}
                        </div>

                        {/* Specialties */}
                        <div className="form-group firstformgroup">
                            <label htmlFor="specialties">Specialties</label>
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                components={animatedComponents}
                                options={[
                                    { label: "No Specialty", value: "no_specialty" },
                                    ...Specialities.map(spec => ({
                                        label: spec.name,
                                        value: spec._id,
                                    }))
                                ]}
                                value={
                                    formData.specialties.includes('no_specialty')
                                        ? [{ label: "No Specialty", value: "no_specialty" }]
                                        : Specialities
                                            .filter(spec => formData.specialties.includes(spec._id))
                                            .map(spec => ({
                                                label: spec.name,
                                                value: spec._id,
                                            }))
                                }
                                onChange={handleSpecialtiesChange}
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        borderColor: '#0056B3',
                                        border: '2px solid #0056B3',
                                        borderRadius: '10px',
                                        minHeight: '50px',
                                    }),
                                }}
                            />
                        </div>
                    </>

                    <div className='row'>
                        {/* Firstname */}
                        <div className="form-group col-md-6">
                            <label htmlFor="firstname">Firstname</label>
                            <input
                                type="text"
                                name="firstname"
                                id="firstname"
                                placeholder="John Doe"
                                value={formData.firstname || user?.firstname}
                                onChange={handleInputChange}
                                disabled
                            />
                            {errors.firstname && <div className="text-danger">{errors.firstname}</div>}
                        </div>

                        {/* Lastname */}
                        <div className="form-group col-md-6">
                            <label htmlFor="lastname">Lastname</label>
                            <input
                                type="text"
                                name="lastname"
                                id="lastname"
                                placeholder="johndoe"
                                value={formData.lastname || user?.lastname}
                                onChange={handleInputChange}
                                disabled
                            />
                            {errors.lastname && <div className="text-danger">{errors.lastname}</div>}
                        </div>
                    </div>

                    {/* Username */}
                    <div className="form-group">
                        <label htmlFor="username">Username</label>
                        <input
                            type="text"
                            name="username"
                            id="username"
                            placeholder="johndoe"
                            value={formData.username || user?.username}
                            onChange={handleInputChange}
                        />
                        {errors.username && <div className="text-danger">{errors.username}</div>}
                    </div>

                    <div className='row'>
                        {/* Type */}
                        <div className="form-group col-md-6">
                            <label htmlFor="type">Type</label>
                            <select
                                name="type"
                                id="type"
                                onChange={handleInputChange}
                                value={formData.type || user?.type}
                            >
                                <option value="">Select Type</option>
                                {['Permanent', 'Consultant'].map(type => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                            {errors.type && <div className="text-danger">{errors.type}</div>}
                        </div>

                        {/* Civility */}
                        <div className="form-group col-md-6">
                            <label htmlFor="civility">Civility</label>
                            <select
                                name="civility"
                                id="civility"
                                onChange={handleInputChange}
                                value={formData.civility || user?.civility}
                            >
                                <option value="">Select Civility</option>
                                {['M.', 'Mme.'].map(civility => (
                                    <option key={civility} value={civility}>
                                        {civility}
                                    </option>
                                ))}
                            </select>
                            {errors.civility && <div className="text-danger">{errors.civility}</div>}
                        </div>
                    </div>

                    {/* Role */}
                    <div className="form-row row">
                        <div className="form-group col-md-12">
                            <label htmlFor="role">Fonction</label>
                            <select
                                name="role"
                                id="role"
                                onChange={handleRoleChange}
                                value={formData.role || user?.role?._id}
                            >
                                <option value="">Select Role</option>
                                {Roles.map(role => (
                                    <option key={role?._id} value={role?._id}>
                                        {role.name.replace(/^medtinz_client_/, '').replace(/_/g, ' ')}
                                    </option>
                                ))}
                            </select>
                            {errors.role && <div className="text-danger">{errors.role}</div>}
                        </div>
                    </div>

                    {/* Permissions - Show only if a role is selected */}
                    {formData.role && (
                        <div
                            className="form-group"
                            style={{
                                marginBottom: '10px',
                                marginTop: '20px',
                                backgroundColor: '#f9f9f9',
                                position: 'relative',
                                paddingTop: '10px',
                            }}
                        >
                            <label
                                style={{
                                    fontSize: '16px',
                                    color: '#333',
                                    position: 'absolute',
                                    top: '-10px',
                                    left: '0',
                                }}
                            >
                                Permissions
                            </label>
                            <Select
                                closeMenuOnSelect={false}
                                components={animatedComponents}
                                isMulti
                                options={allPermissions.map((permission) => ({
                                    label: permission.replace(/^medtinz_client_/, '').replace(/_/g, ' '),
                                    value: permission,
                                }))}
                                onChange={handlePermissionsChange}
                                value={formData.permissions.map((permission) => ({
                                    label: permission.replace(/^medtinz_client_/, '').replace(/_/g, ' '),
                                    value: permission,
                                }))}
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        borderColor: '#0056B3',
                                        border: '2px solid #0056B3',
                                        borderRadius: '10px',
                                        minHeight: '50px',
                                    }),
                                }}
                            />
                        </div>
                    )}

                    {/* Email */}
                    <div className="form-row row">
                        <div className="form-group col-md-12">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                name="email"
                                id="email"
                                placeholder="johndoe@example.com"
                                value={formData.email || user?.email}
                                onChange={handleInputChange}
                                disabled
                            />
                            {errors.email && <div className="text-danger">{errors.email}</div>}
                        </div>
                    </div>

                    {/* Password and Confirm Password */}
                    <div className="form-row row">
                        <div className="form-group col-md-6">
                            <label htmlFor="password">Password</label>
                            <input
                                type="password"
                                name="password"
                                id="password"
                                placeholder="Password123!"
                                value={formData.password}
                                onChange={handleInputChange}
                            />
                            {errors.password && <div className="text-danger">{errors.password}</div>}
                        </div>
                        <div className="form-group col-md-6">
                            <label htmlFor="confirmPassword">Confirm Password</label>
                            <input
                                type="password"
                                name="confirmPassword"
                                id="confirmPassword"
                                placeholder="Password123!"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                            />
                            {errors.confirmPassword && <div className="text-danger">{errors.confirmPassword}</div>}
                        </div>
                    </div>

                    {/* Phone and Address */}
                    <div className="form-row row">
                        <div className="form-group col-md-6">
                            <label htmlFor="contact.phone">Phone</label>
                            <input
                                type="tel"
                                name="contact.phone"
                                id="contact.phone"
                                placeholder="0123456789"
                                value={formData.contact.phone || user?.contact?.phone}
                                onChange={handleInputChange}
                            />
                            {errors['contact.phone'] && <div className="text-danger">{errors['contact.phone']}</div>}
                        </div>
                        <div className="form-group col-md-6">
                            <label htmlFor="contact.address">Address</label>
                            <input
                                type="text"
                                name="contact.address"
                                id="contact.address"
                                placeholder="123 Main St"
                                value={formData.contact.address || user?.contact?.address}
                                onChange={handleInputChange}
                            />
                            {errors['contact.address'] && <div className="text-danger">{errors['contact.address']}</div>}
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="d-flex justify-content-end">
                        <CButton type="submit" className="button-sm buttonsubmit" style={{ marginTop: '15px' }}>
                            Save Changes
                        </CButton>
                    </div>
                </form>
            </CModalBody>
        </CModal>
    );
};

export default EditStaffModal;